import './DataTable.scss';
import {map, get, values, isArray} from 'lodash';
import React, {useState, useMemo, useEffect} from 'react';
import {Button} from '../Form/Button/Button';
import {buttonColors} from '../Pickers/InputTheme';

/**
 * Reusable data table component with column headers and row data.
 * Supports error highlighting, row indexing, pagination, and responsive scrolling.
 *
 * @param {object} params - The component parameters
 * @param {Array<object>} params.columns - Array of column definitions
 * @param {string} params.columns[].key - Unique key for the column
 * @param {string} params.columns[].label - Display label for column header
 * @param {string} [params.columns[].className] - Optional CSS class for the column
 * @param {number} [params.columns[].width] - Optional width in pixels for the column
 * @param {function} [params.columns[].render] - Optional custom render function for cell content
 * @param {Array<object>|object} params.data - Array or object of data to display (objects will be converted to arrays)
 * @param {object} [params.errors] - Optional errors object with row index keys
 * @param {boolean} [params.showIndex=false] - Whether to show row index column
 * @param {string} [params.indexLabel=''] - Label for the index column header
 * @param {string} [params.className] - Optional additional CSS class for the table container
 * @param {string} [params.emptyMessage] - Message to display when no data
 * @param {function} [params.getRowKey] - Function to get unique key for each row
 * @param {number} [params.pageSize=25] - Number of items per page (default 25)
 * @param {boolean} [params.showPagination=false] - Whether to show pagination controls
 * @returns {JSX.Element} The data table component
 *
 * @example
 * <DataTable
 *   columns={[
 *     { key: 'firstName', label: 'First Name', width: 150 },
 *     { key: 'email', label: 'Email', width: 300 }
 *   ]}
 *   data={users}
 *   showIndex={true}
 *   errors={validationErrors}
 *   pageSize={5}
 *   showPagination={true}
 * />
 */
export const DataTable = ({
    columns,
    data,
    errors = {},
    showIndex = false,
    indexLabel = '',
    className = '',
    emptyMessage,
    getRowKey = (row, index) => row.id || index,
    pageSize = 25,
    showPagination = false
}) => {
    const [currentPage, setCurrentPage] = useState(0);

    // Convert object to array if needed (for cases where data is an object with numeric keys)
    const dataArray = isArray(data) ? data : values(data);

    // Pagination calculations
    const totalPages = Math.ceil(dataArray.length / pageSize);
    const paginatedData = useMemo(() => {
        if (!showPagination) {
            return dataArray;
        }
        const startIndex = currentPage * pageSize;
        const endIndex = startIndex + pageSize;
        return dataArray.slice(startIndex, endIndex);
    }, [dataArray, currentPage, pageSize, showPagination]);

    // Reset to first page when data changes
    useEffect(() => {
        setCurrentPage(0);
    }, [dataArray.length]);

    if (!dataArray || dataArray.length === 0) {
        if (!emptyMessage) {
            return null;
        }
        return (
            <div className={`dataTable ${className}`}>
                <div className="emptyMessage">{emptyMessage}</div>
            </div>
        );
    }

    // Calculate total width based on column widths and grid gap
    // Keep this in sync with the SCSS gap value defined in DataTable.scss (.tableHeaders/.tableRow -> gap: 12px)
    const GRID_GAP_PX = 12;
    const numberOfColumns = columns.length + (showIndex ? 1 : 0);
    const baseColumnsWidth = columns.reduce((sum, col) => sum + (col.width || 150), 0) + (showIndex ? 60 : 0);
    const gapsTotalWidth = Math.max(numberOfColumns - 1, 0) * GRID_GAP_PX;
    const totalWidth = baseColumnsWidth + gapsTotalWidth;

    // Build grid template columns string
    const gridTemplateColumns = [
        showIndex && '60px',
        ...map(columns, col => `${col.width || 150}px`)
    ].filter(Boolean).join(' ');

    /**
     * Renders a cell value with error highlighting if applicable.
     *
     * @param {object} params - Cell render parameters
     * @param {object} params.row - The row data object
     * @param {object} params.column - The column definition
     * @param {number} params.rowIndex - The index of the current row
     * @returns {JSX.Element} The rendered cell
     */
    const renderCell = ({row, column, rowIndex}) => {
        const cellValue = column.render 
            ? column.render({row, rowIndex})
            : get(row, column.key);
        
        const hasError = get(errors, `[${rowIndex}].${column.key}`);
        const cellClassName = `${column.className || column.key} ${hasError ? 'error' : ''}`;
        
        return (
            <div key={column.key} className={cellClassName}>
                {cellValue}
            </div>
        );
    };

    return (
        <div className={`dataTable ${className}`}>
            {/* Table Headers */}
            <div 
                className="tableHeaders"
                style={{
                    width: `${totalWidth}px`,
                    gridTemplateColumns
                }}
            >
                {showIndex && <div className="countCol">{indexLabel}</div>}
                {map(columns, (column) => (
                    <div 
                        key={column.key} 
                        className={`tableHeader ${column.className || column.key}`}
                    >
                        {column.label}
                    </div>
                ))}
            </div>

            {/* Table Rows */}
            <div className="tableRows">
                {map(paginatedData, (row, rowIndex) => {
                    // Calculate actual row index considering pagination
                    const actualRowIndex = showPagination ? (currentPage * pageSize) + rowIndex : rowIndex;
                    
                    return (
                        <div
                            key={getRowKey(row, actualRowIndex)}
                            className="tableRow"
                            style={{
                                width: `${totalWidth}px`,
                                gridTemplateColumns
                            }}
                        >
                            {showIndex && (
                                <div className="countCol">{actualRowIndex + 1}</div>
                            )}
                            {map(columns, (column) => renderCell({row, column, rowIndex: actualRowIndex}))}
                        </div>
                    );
                })}
            </div>

            {/* Pagination Controls */}
            {showPagination && totalPages > 1 && (
                <div className="pagination-controls">
                    <Button
                        color={buttonColors.GREY}
                        onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                        disabled={currentPage === 0}
                        text="‹"
                        className="pagination-button"
                    />
                    <div className="page-info">
                        {currentPage + 1} / {totalPages}
                    </div>
                    <Button
                        color={buttonColors.GREY}
                        onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                        disabled={currentPage === totalPages - 1}
                        text="›"
                        className="pagination-button"
                    />
                </div>
            )}
        </div>
    );
};

